import React, { useState, useEffect } from "react";
import { ClipboardCheck, CheckCircle2, ShieldCheck, Loader2 } from "lucide-react";
import ApiService from "../../services/Api.service";
import Modal from "../Modal/Modal";
import Button from "../Button/Button";
import toast from "react-hot-toast";

const CampaignTemplateModal = ({ item, onClose, onSave }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(
    item?.template_id || ""
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const res = await ApiService.getTemplates();
        let list = [];
        if (Array.isArray(res?.data)) {
          list = res.data;
        } else if (Array.isArray(res?.data?.data)) {
          list = res.data.data;
        } else if (Array.isArray(res?.data?.subprocesses)) {
          list = res.data.subprocesses;
        }
        setTemplates(list);

        // If not already assigned, but templates exist, we can keep it blank or select first
        if (!selectedTemplateId && item?.template_id) {
          setSelectedTemplateId(item.template_id);
        }
      } catch (err) {
        console.error("Error fetching templates:", err);
        toast.error("Failed to load audit templates");
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [item]);

  const handleSave = async () => {
    if (!selectedTemplateId) {
      toast.error("Please select an audit template");
      return;
    }

    try {
      setSaving(true);
      const res = await ApiService.assignCampaignTemplate(item.id, {
        template_id: selectedTemplateId,
      });

      if (res?.status === 200 || res?.data?.status === 200) {
        toast.success(
          res?.data?.message || "Template successfully assigned to all calls!"
        );
        if (onSave) onSave();
        onClose();
      } else {
        throw new Error(res?.data?.detail || "Failed to assign template");
      }
    } catch (err) {
      console.error("Error saving campaign template:", err);
      toast.error(err.response?.data?.detail || err.message || "Failed to assign template");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Select Audit Template"
      subtitle={`Campaign: ${item?.name || ""}`}
      icon={<ClipboardCheck size={20} color="#4338ca" />}
      maxWidth="36rem"
      footer={
        <>
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSave}
            disabled={saving || loading || templates.length === 0}
          >
            {saving ? (
              <>
                <Loader2 className="animate-spin" size={16} />
                <span>Applying...</span>
              </>
            ) : (
              <>
                <ClipboardCheck size={16} />
                <span>Apply Template</span>
              </>
            )}
          </Button>
        </>
      }
    >
      {/* Informational banner */}
      <div
        style={{
          backgroundColor: "#f0fdf4",
          border: "0.0625rem solid #bbf7d0",
          borderRadius: "0.5rem",
          padding: "0.625rem 0.875rem",
          marginBottom: "1rem",
          display: "flex",
          alignItems: "flex-start",
          gap: "0.5rem",
        }}
      >
        <ShieldCheck size={18} color="#16a34a" style={{ marginTop: "0.125rem", flexShrink: 0 }} />
        <span style={{ fontSize: "0.75rem", color: "#166534", lineHeight: "1.4" }}>
          The selected quality audit template will be automatically applied to <strong>all calls</strong> in this campaign for AI-powered evaluation and scoring.
        </span>
      </div>

      <label
        style={{
          display: "block",
          fontSize: "0.8125rem",
          fontWeight: "700",
          color: "#374151",
          marginBottom: "0.625rem",
          fontFamily: "Nunito, sans-serif",
        }}
      >
        Available Quality Templates
      </label>

      {loading ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "1.875rem 0",
            gap: "0.625rem",
            color: "#64748b",
            fontSize: "0.8125rem",
          }}
        >
          <Loader2 className="animate-spin" size={24} color="#4f46e5" />
          <span>Loading audit templates...</span>
        </div>
      ) : templates.length === 0 ? (
        <div
          style={{
            padding: "1.5rem",
            textAlign: "center",
            backgroundColor: "#f8fafc",
            borderRadius: "0.5rem",
            border: "0.0625rem dashed #cbd5e1",
            color: "#64748b",
            fontSize: "0.8125rem",
          }}
        >
          No audit templates found. Please create one in <strong>Settings → Audit Template</strong> first.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.625rem" }}>
          {templates.map((tmpl) => {
            const tmplId = tmpl.id || tmpl._id || "";
            const isSelected = selectedTemplateId === tmplId;
            const criteriaCount =
              tmpl.sections?.reduce(
                (acc, sec) => acc + (sec.parameters?.length || 0),
                0
              ) || tmpl.criteria_count || 0;

            return (
              <div
                key={tmplId}
                onClick={() => setSelectedTemplateId(tmplId)}
                style={{
                  border: isSelected ? "0.125rem solid #4f46e5" : "0.0625rem solid #e2e8f0",
                  backgroundColor: isSelected ? "#f5f3ff" : "#ffffff",
                  borderRadius: "0.625rem",
                  padding: "0.75rem 0.875rem",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                  <input
                    type="radio"
                    name="campaign_template"
                    value={tmplId}
                    checked={isSelected}
                    onChange={() => setSelectedTemplateId(tmplId)}
                    style={{ cursor: "pointer", width: "1rem", height: "1rem", accentColor: "#4f46e5" }}
                  />
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <span
                        style={{
                          fontSize: "0.875rem",
                          fontWeight: "700",
                          color: isSelected ? "#4338ca" : "#1e293b",
                        }}
                      >
                        {tmpl.name}
                      </span>
                      {item?.template_id === tmplId && (
                        <span
                          style={{
                            fontSize: "0.625rem",
                            fontWeight: "600",
                            backgroundColor: "#dbeafe",
                            color: "#1e40af",
                            padding: "0.125rem 0.375rem",
                            borderRadius: "0.25rem",
                          }}
                        >
                          Current
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#64748b",
                        marginTop: "0.125rem",
                        display: "flex",
                        gap: "0.75rem",
                      }}
                    >
                      {tmpl.process_type && (
                        <span>Type: <strong>{tmpl.process_type}</strong></span>
                      )}
                      {criteriaCount > 0 && (
                        <span>Criteria: <strong>{criteriaCount}</strong></span>
                      )}
                    </div>
                  </div>
                </div>
                {isSelected && (
                  <CheckCircle2 size={18} color="#4f46e5" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};

export default CampaignTemplateModal;
